import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    faArrowRightToBracket,
    faBuilding,
    faHandHoldingBox,
    faMagicWandSparkles,
    faUser,
    faWarehouseFull,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useBreadcrumbs from 'use-react-router-breadcrumbs';

import { SpacefillDebugActions } from '@spacefill/shared/src/components/debug-actions/SpacefillDebugActions';
import { getEnvironmentVariable } from '@spacefill/shared/src/utils/GetEnvVar';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';
import { Breadcrumb } from '@spacefill/uikit/src/components/Breadcrumb';
import { Button } from '@spacefill/uikit/src/components/Button';

import { AiUnreadReplyBadge } from '../pages/analytics-chat/components/AiUnreadReplyBadge';
import { LoadingDots } from '../pages/analytics-chat/components/LoadingDots';
import { getLoadingStatusLabel } from '../pages/analytics-chat/getLoadingStatusLabel';
import { TopBarActionsAndNotifications } from './TopBarActionsAndNotifications';
import { TopBarProfile } from './TopBarProfile';
import { routes } from './TopbarRoutes';
import { useAnalyticsChatPanel } from './useAnalyticsChatPanel';

const SpacefillTopbar = () => {
    const breadcrumbs = useBreadcrumbs(routes);
    const { user, switchRole } = useUserContext();
    const { openChat, isWorking, loadingStatus, hasUnreadReply } = useAnalyticsChatPanel();
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const isCarrier = !!user.carrierId;

    const workingLabel = getLoadingStatusLabel(t, loadingStatus);

    const profileTopNavigation = user.multiRoles
        ? [
              {
                  title: t('Operator view'),
                  hasRoundedIcon: true,
                  icon: <FontAwesomeIcon icon={faWarehouseFull} />,
                  disabled: user.role === 'PROVIDER',
                  dataTestid: `nav-switch-operator${user.role === 'PROVIDER' ? '-disabled' : ''}`,
                  onClick: async () => await switchRole('PROVIDER', () => navigate('/')),
                  conditionForDisplay: () => !isCarrier,
              },
              {
                  title: t('Shipper view'),
                  hasRoundedIcon: true,
                  icon: <FontAwesomeIcon icon={faHandHoldingBox} />,
                  disabled: user.role === 'SHIPPER',
                  dataTestid: `nav-switch-shipper${user.role === 'SHIPPER' ? '-disabled' : ''}`,
                  onClick: async () => await switchRole('SHIPPER', () => navigate('/')),
                  conditionForDisplay: () => !isCarrier,
              },
          ]
        : [];
    const profileNavigation = [
        {
            title: t('Coworkers & Company'),
            hasRoundedIcon: true,
            icon: <FontAwesomeIcon icon={faBuilding} />,
            dataTestid: 'nav-company',
            isActive: location.pathname.startsWith('/company/coworkers'),
            to: '/company/coworkers',
            conditionForDisplay: () => !isCarrier,
        },
        {
            title: t('User profile'),
            hasRoundedIcon: true,
            icon: <FontAwesomeIcon icon={faUser} />,
            dataTestid: 'nav-profile',
            isActive: location.pathname.startsWith('/profile/'),
            to: '/profile/',
        },
        {
            title: t('Log out'),
            hasRoundedIcon: true,
            dataTestId: 'nav-logout',
            icon: <FontAwesomeIcon icon={faArrowRightToBracket} />,
            to: '/logout/',
        },
    ];
    return (
        <div className='flex items-center justify-between bg-gray-50 border-b border-gray-200 px-6 py-2 h-12'>
            <Breadcrumb
                breadcrumbs={breadcrumbs}
                breadcrumbHiddenItems={[/\/view$/]}
            />
            <div className='flex items-center gap-4'>
                {user.features.askAnythingEnabled && (
                    <div className='relative'>
                        <div className='relative rounded-md'>
                            <Button
                                variant='dark'
                                size='small'
                                iconStart={
                                    isWorking ? (
                                        <LoadingDots />
                                    ) : hasUnreadReply ? (
                                        <AiUnreadReplyBadge
                                            className='pointer-events-none'
                                            label={t('New AI reply')}
                                        />
                                    ) : (
                                        <FontAwesomeIcon icon={faMagicWandSparkles} />
                                    )
                                }
                                onClick={openChat}
                            >
                                {isWorking ? workingLabel : t('AI Insights')}
                            </Button>
                        </div>
                    </div>
                )}
                {(user.isStaff || getEnvironmentVariable('featureTogglingEnabled')) && <SpacefillDebugActions />}
                <TopBarActionsAndNotifications />
                <TopBarProfile
                    profileTopNavigation={profileTopNavigation}
                    profileNavigation={profileNavigation}
                    userInfo={{
                        userName: `${user?.firstName} ${user?.lastName}`,
                        company: user?.customer?.company || user?.logisticProvider?.company || user?.carrier?.company,
                    }}
                />
            </div>
        </div>
    );
};

export { SpacefillTopbar };
